import { Component, OnInit } from '@angular/core';
import { CommonModule, NgForOf } from '@angular/common';
import { ThemeService } from '../../services/theme-service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {catchError, combineLatest, map, Observable, of, startWith, switchMap, take} from 'rxjs';
import { LoadingSpinnerComponent } from "../ui/loading-spinner-component";
import { ApiService } from '../../services/api.service';
import { ReceiptDetails, ReceiptDetailsPostAction, ReceiptDetailsStation } from '../../models/ReceiptDetails';
import { DossierViewerComponent } from "../ui/dossier-viewer/dossier-viewer.component";
import { AuthService } from '../../services/auth.service';
import { DraggerComponent } from "../ui/dragger.component";
import { AuthorizedApiService } from '../../services/authorized-api.service';
import { OrderService } from '../../services/order.service';
import { TranslateService } from '../../services/translate.service';
import MoneyUtils from '../../utils/Utils';
import { SnackbarService } from '../../services/snackbar.service';
import { ModalService } from '../../services/modal.service';
import { ReceiptEmailModalComponent } from '../dialogs/receipt-email-dialog.component';
import { CircleButtonComponent } from '../buttons/circle-button.component';
import { PageButtonComponent } from "../buttons/page-button.component";
import { UrlHistoryService } from '../../services/url.history.service';
import {LinkButtonComponent} from '../buttons/link-button.component';
import {AppStateService} from '../../services/app-state.service';
import {AppState} from '../../models/AppState';

@Component({
  selector: 'app-receipt-list',
  standalone: true,
  imports: [
    NgForOf,
    LoadingSpinnerComponent,
    RouterLink,
    CommonModule,
    CircleButtonComponent,
    DossierViewerComponent,
    DraggerComponent,
    PageButtonComponent,
    LinkButtonComponent
  ],
  templateUrl: './receipt.component.html',
  styleUrl: './receipt.component.css',
})
export class ReceiptComponent implements OnInit {

  showBump = false;
  showTicket = false;
  showQR = false;
  useModernVouchers = true;
  private appState?: AppState;

  constructor(
    private themeService: ThemeService,
    private api: ApiService,
    private route: ActivatedRoute,
    private auth: AuthService,
    private router: Router,
    private authorizedApi: AuthorizedApiService,
    private orderService: OrderService,
    public translate: TranslateService,
    private snackbar: SnackbarService,
    private modalService: ModalService,
    private identityService: UrlHistoryService,
    private appStateService: AppStateService
  ) {}

  vm$!: Observable<{ loading: boolean; receiptDetails: ReceiptDetails | undefined; error: string | undefined }>;
  venueId: string = "";
  isKiosk!: boolean;
  isMonter!: boolean;
  bumpState = { sending: false, save_email: 0, entered_code: "", error: false, bump: "i", bump_station: "" };
  moneyUtils = MoneyUtils;
  ngOnInit(): void {
    this.isKiosk = this.auth.isKioskMode();
    this.isMonter = this.auth.getKioskState() === "monter";

    // this.sub1 = combineLatest([
    //   this.route.paramMap,
    //   this.route.queryParamMap,
    //   this.appStateService.observeAppState()
    // ])

    this.vm$ = combineLatest([
      this.route.paramMap,
      this.appStateService.observeAppState()
    ]).pipe(
      switchMap(res => {
        const pm = res[0];
        this.appState = res[1];
        this.useModernVouchers = this.appState?.cfg?.voucher?.collins_voucher_mode === "modern";
        const venueId = pm.get("venue_id");
        this.venueId = venueId || "";
        const receiptId = pm.get("receipt_id");
        console.log('Fetching receipt details for', pm);
        if (!venueId || !receiptId) {
          return of({
            loading: false,
            receiptDetails: undefined,
            error: this.translate.getString("Kvitto ej angivet")
          });
        }
        console.log('Fetching receipt details for', venueId, receiptId);
        return this.api.getReceiptDetails(venueId, receiptId, true).pipe(
          map((details: ReceiptDetails) => ({
            loading: false,
            receiptDetails: details,
            error: undefined
          })),
          startWith({
            loading: true,
            receiptDetails: undefined,
            error: undefined
          }),
          catchError(err => {
            console.error('Error fetching receipt details:', err);
            return of({
              loading: false,
              receiptDetails: undefined,
              error: err?.message || this.translate.getString("Något gick fel")
            });
          })
        );
      }
      )
    );
  }

  ngOnDestroy(): void {
  }

  printVoucher(voucherKey: string, station: ReceiptDetailsStation): void {
    this.bumpState.sending = true;
    this.bumpState.bump_station = station.name;
    this.authorizedApi.printVoucher(voucherKey, station.id).subscribe(r => {
      this.bumpState.sending = false;
      this.bumpState.bump = "s";
      console.log(`Voucher successfully sent to ${station.name}`);
    }, error => {
      this.bumpState.sending = false;
    });
  }

  // sendMail() {
  //   // TODO activate send mail directly to email account, use this when we have support for apple anon emails
  //   this.snackbar.showMessage("Kvittokopia skickad till din e-post", 2000, "");
  // }

  sendReceipt(r: ReceiptDetails): void {
    this.modalService.show(ReceiptEmailModalComponent, {
      data: {
        action: "send_receipt",
        // The server mails the document the page shows: on a Norwegian venue the receipt itself
        // when it is the electronic original (electronic_original), otherwise a copy
        title: this.translate.getString(r.electronic_original ? "Skicka kvitto" : "Skicka kvittokopia"),
        receiptKey: r.key,
        venueId: this.venueId,
        confirmText: this.getString("Skicka"),
        abortText: this.getString("Avbryt"),
        mailRecipient: this.orderService.getOrderOrNull()?.user_cfg?.receipt_mail
      }
    });
  }

  sendTicket(voucherId: string): void {
    this.modalService.show(ReceiptEmailModalComponent, {
      data: {
        action: "send_voucher",
        title: "Skicka biljett",
        venueId: this.venueId,
        voucherId: voucherId,
        confirmText: this.getString("Skicka"),
        abortText: this.getString("Avbryt"),
        mailRecipient: this.orderService.getOrderOrNull()?.user_cfg?.receipt_mail
      }
    });
  }

  sendAllTickets(r: ReceiptDetails) {
    this.modalService.show(ReceiptEmailModalComponent, {
      data: {
        action: "send_all_vouchers",
        title: "Skicka biljetter",
        receiptKey: r.key,
        confirmText: this.getString("Skicka"),
        abortText: this.getString("Avbryt"),
        venueId: this.venueId,
        mailRecipient: this.orderService.getOrderOrNull()?.user_cfg?.receipt_mail
      }
    });
  }

  getGiftcardName(pa: ReceiptDetailsPostAction) {
    const receiver_name = pa.spec?.receiver_name;
    if (receiver_name) {
      return receiver_name;
    }
    return "";
  }

  openGiftcard(pa: ReceiptDetailsPostAction) {
    this.router.navigateByUrl(`${this.venueId}/giftcard/${pa.unique_id}`);
  }

  goToStartpage() {
    this.router.navigateByUrl(`${this.venueId}/menu/food`);
  }

  getString(s: string, postCase = "normal") {
    return this.translate.single(s, postCase);
  }

  back() {
    this.identityService.navigateBack();
  }

}
